import { IconSearch } from "@tvg/fuse/icon/all";
import { SrOnly } from "@tvg/fuse/utils/sr-only";
import { useMainState } from "client/state/Main";
import { capitaliseFirstChar } from "common/utils/capitaliseFirstChar";
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  handleOpenModal,
  submitSearch,
  trackSearchEvent,
} from "../../../utils/searchUtils";
import SearchOverlay from "./components/SearchOverlay";
import styles from "./Search.module.css";

const Search: React.FC = () => {
  const brand = useMainState((state) => state.brand);
  const { isSearchActive, searchTerm, setIsSearchActive, setSearchTerm } =
    useMainState();

  const [blockInput, setBlockInput] = useState(true);

  const searchRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const formRef = useRef<HTMLFormElement | null>(null);
  const [localSearchTerm, setLocalSearchTerm] = useState(searchTerm);
  const [isInputFocused, setIsInputFocused] = useState(false);

  const placeholderText: string = `Search ${capitaliseFirstChar(brand === "littlewoods" ? brand : "Very")}`;

  // Update local search term when global search term changes
  useEffect(() => {
    setLocalSearchTerm(searchTerm);
  }, [searchTerm]);

  // Handle autosuggest modal opening
  const handleAutosuggestOpen = useCallback(
    async (e: React.FocusEvent | React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      // Track search interaction
      trackSearchEvent("search_focus", "Real Estate", "Header", "Search");

      const modalOpened = await handleOpenModal(e);

      if (!modalOpened) {
        setBlockInput(false);
      }
    },
    [],
  );

  // Handle input change
  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      setLocalSearchTerm(newValue);
      setSearchTerm(newValue);
    },
    [setSearchTerm],
  );

  // Handle form submission
  const handleSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      if (localSearchTerm && localSearchTerm.trim().length > 0) {
        submitSearch(localSearchTerm);
      } else {
        // If no search term, focus back to input
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }
    },
    [localSearchTerm],
  );

  // Handle search close (explicit close like Escape or click outside)
  const handleSearchClose = useCallback(
    (
      e?:
        | React.KeyboardEvent<HTMLDivElement>
        | React.MouseEvent<HTMLDivElement>,
    ) => {
      setIsSearchActive(false);
      setIsInputFocused(false);
    },
    [setIsSearchActive, setIsInputFocused],
  );

  useEffect(() => {
    const searchInput = inputRef.current;
    const searchBtn =
      searchInput?.parentElement?.querySelector<HTMLButtonElement>(
        "button[data-testid='complex-header-search-button']",
      );

    if (!searchInput) return;

    if (blockInput) {
      // disable input
      searchInput.tabIndex = -1;
      searchInput.setAttribute("aria-hidden", "true");
      searchInput.setAttribute("aria-disabled", "true");

      if (searchBtn) {
        searchBtn.tabIndex = -1;
        searchBtn.setAttribute("aria-hidden", "true");
      }
    } else {
      // revert back
      searchInput.tabIndex = 0;
      searchInput.setAttribute("aria-hidden", "false");
      searchInput.setAttribute("aria-disabled", "false");
      searchInput.focus();

      if (searchBtn) {
        searchBtn.tabIndex = 0;
        searchBtn.setAttribute("aria-hidden", "false");
      }
    }
  }, [blockInput]);

  return (
    <>
      <div
        ref={searchRef}
        className={`${styles.search} navi-header-fe-search`}
        data-testid="header-fe-search"
      >
        <form
          ref={formRef}
          className={`${styles.search__form} header-fe-search__form`}
          onSubmit={handleSubmit}
          role="search"
        >
          <SrOnly htmlFor="complex-header-search-input-id">
            {placeholderText}
          </SrOnly>

          <input
            ref={inputRef}
            className={`${styles.search__input} header-fe-search__input`}
            id="complex-header-search-input-id"
            type="search"
            name="search"
            value={localSearchTerm}
            placeholder={!blockInput && isInputFocused ? "" : placeholderText}
            aria-label={placeholderText}
            data-testid="search-input"
            onFocus={() => {
              setIsSearchActive(true);
              !blockInput && setIsInputFocused(true);
            }}
            onClick={() => {
              trackSearchEvent(
                "search_focus",
                "Real Estate",
                "Header",
                "Search",
              );
            }}
            onChange={handleInputChange}
            onBlur={() => {
              if (!blockInput) {
                setIsInputFocused(false);
                setIsSearchActive(false);
              }
            }}
          />

          <button
            className={`${styles.search__iconButton} header-fe-search__button`}
            type="submit"
            aria-label="Search"
            data-testid="complex-header-search-button"
            onFocus={() => {
              !blockInput && setIsSearchActive(true);
              !blockInput && setIsInputFocused(true);
            }}
            onBlur={() => {
              setIsSearchActive(false);
            }}
          >
            <IconSearch
              className={`${styles.search__searchSVG} header-fe-search__search-svg`}
            />
          </button>
        </form>
        {!blockInput ? (
          <div />
        ) : (
          <input
            className={`${styles.search__inputOverlay} header-fe-search__input-overlay`}
            data-testid="search-autosuggest-modal"
            aria-label={`Search ${capitaliseFirstChar(
              brand === "littlewoods" ? brand : "Very",
            )}`}
            placeholder={isInputFocused ? "" : placeholderText}
            tabIndex={0}
            readOnly
            onFocus={(e) => {
              setIsInputFocused(true);
              handleAutosuggestOpen(e);
            }}
            onClick={handleAutosuggestOpen}
          />
        )}
      </div>

      <SearchOverlay
        disableBackgroundScroll={isSearchActive}
        dataTestId="navi-header--search--overlay"
        active={isSearchActive}
        inputRef={inputRef}
        escapeKeyPress={(e) => {
          handleSearchClose(e);
        }}
        onBackgroundClick={(e) => {
          handleSearchClose(e);
        }}
      />
    </>
  );
};

export default Search;
